import "./Spreadsheet.scss";
import Button from "core/buttons/button/Button";
import DiscardChangesModal from "components/modal/DiscardChangesModal";
import Loader from "core/loader/LoaderV2";
import Pagination from "core/pagination/Pagination";
import SpreadsheetFilters from "components/form/spreadsheet/SpreadsheetFilters";
import SpreadsheetTable from "components/form/spreadsheet/SpreadsheetTable";
import { FormattedMessage, useIntl } from "react-intl";
import { useLocalStorage } from "react-use";
import {
  DEFAULT_STATUS_MISSION,
  FILTERS_FOR_NAMES,
  QUERY_STATUS,
  VISIBLE_COLUMNS_LIST,
} from "constants/constants";
import { useBranchContext } from "api/settings/branch/BranchProviders";
import { useClientContext } from "api/directory/client/ClientProvider";
import { useEffect, useRef, useState } from "react";
import { useMissionBillContext } from "api/mission/mission-bills/MissionBillsProvider";
import { useMissionContext } from "api/mission/MissionProvider";
import { useNavigate, useLocation } from "react-router-dom";
import { usePersonContext } from "api/directory/person/PersonProvider";
import { useUserContext } from "api/user/UserProvider";
import {
  formatPartialMissionsWithBills,
  sortVisibleColumns,
} from "utils/missionHelper";
import ScrollToTop from "core/buttons/scroll-buttons/ScrollToTop";
import { useGeneralContext } from "api/directory/general/GeneralProvider";
import ValidationModal from "components/modal/ValidationModal";
import { includes, uniq } from "lodash";
import { apiResponseHandler } from "utils/apiError";
import { customMessagesFormatted } from "utils/modalsHelper";

const Spreadsheet = () => {
  const location = useLocation();
  const isFromMissionDetail = location.state?.isFromMissionDetail;
  const intl = useIntl();
  const { getClientsByName, clientsByName } = useClientContext();
  const { getPersonsByProfile, personsByProfileList } = usePersonContext();
  const { getNamesByFilters, namesByFilters } = useGeneralContext();
  const { branchList, branchListGetter } = useBranchContext();

  const { userScope, setApiErrorResponse, apiErrorResponse } = useUserContext();

  const {
    missionBillsByParameters,
    searchMissionBillsByParameters,
    loadMissionStatus,
    total,
    selectedDateObj,
    setSelectedDateObj,
  } = useMissionBillContext();

  const {
    faesFilter,
    setFaesFilter,
    editMissionsPartialWithBills,
    getMissionById,
    setDisplayMission,
    editMissionStatus,
    setEditMissionStatus,
    missionsEdited,
    setMissionsEdited,
  } = useMissionContext();
  const navigate = useNavigate();
  const [successMissions, setSuccessMissions] = useState([]);
  const [errorMissions, setErrorMissions] = useState([]);
  const [changedMissionsIds, setChangedMissionsIds] = useState([]);
  const [clonedMissionList, setClonedMissionList] = useState([]);
  const [isOpenModal, setIsOpenModal] = useState(false);
  const [currentPagePending, setCurrentPagePending] = useState();
  const [selectedDatePending, setSelectedDatePending] = useState(new Date());
  const [storedSpreadsheetInfos, setStoredSpreadsheetInfos] = useLocalStorage(
    "spreadSheet_filters",
    {}
  );
  const {
    isFromDashboardCaFae,
    month: caFaeMonth,
    year: caFaeYear,
  } = location.state || {};

  const isFromDashboardCaFaeRef = useRef(isFromDashboardCaFae);

  const {
    currentPage: storedSpreadsheetCurrentPage,
    filters: storedSpreadsheetFilters,
  } = storedSpreadsheetInfos || {};

  const {
    client_id: client_id_stored,
    manager_id: manager_id_stored,
    worker_id: worker_id_stored,
    adv_id: adv_id_stored,
    signed_by_id: signed_by_id_stored,
    charged_by_id: charged_by_id_stored,
    year: year_stored,
    month: month_stored,
    name: name_stored,
    status: status_stored,
    to_bill_only: to_bill_only_stored,
    mission_id: mission_id_stored,
  } = storedSpreadsheetFilters || {};

  const [currentPage, setCurrentPage] = useState(
    storedSpreadsheetCurrentPage || 1
  );
  const [isOpen, setIsOpen] = useState(false);
  const [visibleColumns, setVisibleColumns] = useState(
    sortVisibleColumns(
      VISIBLE_COLUMNS_LIST.map((elm) => ({
        label: intl.formatMessage(elm.label),
        value: elm.value,
        index: elm.index,
      }))
    )
  );
  const [selectedDate_, setSelectedDate_] = useState(() => {
    if (caFaeYear && caFaeMonth) {
      return new Date(`${caFaeYear}-${caFaeMonth}-01`);
    }
    return new Date();
  });

  const [filters, setFilters] = useState({
    client_id: client_id_stored || [],
    manager_id: manager_id_stored || [],
    worker_id: worker_id_stored || [],
    adv_id: adv_id_stored || [],
    signed_by_id: signed_by_id_stored || [],
    charged_by_id: charged_by_id_stored || [],
    year: year_stored || selectedDate_.getFullYear(),
    month: month_stored || selectedDate_.getMonth() + 1,
    name: name_stored || name_stored || "",
    status: status_stored || DEFAULT_STATUS_MISSION,
    to_bill_only: to_bill_only_stored || faesFilter,
    mission_id: mission_id_stored || 0,
    page: storedSpreadsheetCurrentPage || 1,
  });

  useEffect(() => {
    editMissionStatus !== QUERY_STATUS.INITIAL && setIsOpen(true);
    apiResponseHandler({
      status: editMissionStatus,
      missionsEdited,
      successMissions,
      setSuccessMissions,
      changedMissionsIds,
      setChangedMissionsIds,
      apiErrorResponse,
      missionBillsByParameters,
      errorMissions,
      setErrorMissions,
    });
  }, [apiErrorResponse, editMissionStatus]);

  useEffect(() => {
    isFromMissionDetail && setStoredSpreadsheetInfos({});
    setFaesFilter(false);
    setSuccessMissions([]);
    setErrorMissions([]);
    setChangedMissionsIds([]);
    setEditMissionStatus(QUERY_STATUS.INITIAL);
    branchListGetter();
    document.body.style.overflowY = "hidden";
    window.scrollTo(0, 0);
    setApiErrorResponse(null);
  }, []);

  useEffect(() => {
    if (Object.getOwnPropertyNames(selectedDateObj).length > 0) {
      const { month, year } = selectedDateObj;
      const formatDate = new Date(`${year}-${month}-01`);

      if (formatDate.getTime() !== selectedDate_.getTime()) {
        setSelectedDate_(formatDate);
      }
    }

    return () => {
      setSelectedDateObj({});
    };
  }, [faesFilter]);

  useEffect(() => {
    if (missionBillsByParameters?.length) {
      const workersIds = uniq(missionBillsByParameters.map((e) => e.worker_id));
      const clientIds = uniq(
        missionBillsByParameters.map((e) => e.client_id).filter(Boolean)
      );
      getNamesByFilters({
        id: [...workersIds, ...clientIds],
        types: [FILTERS_FOR_NAMES.PERSON, FILTERS_FOR_NAMES.CLIENT],
      });
    }
  }, [missionBillsByParameters]);

  const getMission = ({ id, slug }) => {
    slug &&
      navigate(`/${userScope}/missions/${slug}`, {
        state: { from: "spreadsheet" },
      });
    setDisplayMission(true);
    getMissionById(id);
  };

  const searchWithFilters = ({ page }) => {
    const fromDashboard = isFromDashboardCaFaeRef.current;
    const params = {
      name: filters.name,
      year: fromDashboard ? caFaeYear : selectedDate_.getFullYear(),
      month: fromDashboard ? caFaeMonth : selectedDate_.getMonth() + 1,
      status: fromDashboard ? [] : filters.status?.map((elm) => elm.value),
      to_bill_only: fromDashboard || faesFilter,
      manager_id: filters.manager_id?.map((elm) => elm.value),
      client_id: filters.client_id?.map((elm) => elm.value),
      signed_by_id: filters.signed_by_id?.map((elm) => elm.value),
      charged_by_id: filters.charged_by_id?.map((elm) => elm.value),
      worker_id: filters.worker_id?.map((elm) => elm.value),
      adv_id: filters.adv_id?.map((elm) => elm.value),
      page,
    };

    searchMissionBillsByParameters(params);

    if (fromDashboard) {
      isFromDashboardCaFaeRef.current = false;
      navigate(location.pathname, { replace: true, state: {} });
    }
  };

  useEffect(() => {
    searchWithFilters({
      page: storedSpreadsheetCurrentPage || 1,
    });
    setCurrentPage(storedSpreadsheetCurrentPage || 1);
  }, [selectedDate_, userScope]);

  const getCurrentPageContent = (page) => {
    if (changedMissionsIds?.length === 0) {
      setCurrentPage(page);
      searchWithFilters({ page });
      window.scrollTo(0, 0);
    } else {
      setCurrentPagePending(page);
      setIsOpenModal(true);
    }
  };

  const saveSpreadsheetChanges = (missionId) => {
    if (changedMissionsIds?.length > 0) {
      const changedData = clonedMissionList.filter((elm) =>
        missionId
          ? elm.mission_id === missionId
          : includes(changedMissionsIds, elm.mission_id)
      );
      editMissionsPartialWithBills({
        data: formatPartialMissionsWithBills(changedData),
      });
    }
  };

  const onValidateChangeDate = () => {
    setSelectedDate_(selectedDatePending);
    setCurrentPage(1);
  };

  const onValidateChangePage = () => {
    setCurrentPage(currentPagePending);
    searchWithFilters({ page: currentPagePending });
  };

  const onCloseHandler = () => {
    setIsOpen(false);
    editMissionStatus !== QUERY_STATUS.ERROR && setApiErrorResponse([]);
    setEditMissionStatus(QUERY_STATUS.INITIAL);
    setSuccessMissions(
      successMissions?.map((mission) => {
        return { ...mission, isNewUpdate: false };
      })
    );
    setErrorMissions(
      errorMissions?.map((mission) => {
        return { ...mission, isNewUpdate: false };
      })
    );
    setMissionsEdited([]);
  };

  const renderNoResultsMessage = () =>
    missionBillsByParameters?.length === 0 &&
    loadMissionStatus === QUERY_STATUS.SUCCESS && (
      <div className="empty_filter_info">
        <FormattedMessage
          id="empty_filter_info"
          defaultMessage="Aucune mission avec ces critères"
        />
      </div>
    );

  return (
    <div>
      <ValidationModal
        messages={{
          pendingMessageId: "modal_validation_spreadsheet_pending_msg",
          successMessageId: "modal_validation_spreadsheet_success_msg",
          customMessages: customMessagesFormatted({
            missions: [...successMissions, ...errorMissions],
            apiErrorResponse,
          }),
        }}
        open={isOpen}
        status={editMissionStatus}
        onClose={() => {
          onCloseHandler();
        }}
        isCustomModale={true}
        customTitle={{
          id: "modal_spreadsheet_editing_title",
          defaultMessage: "Edition du tableau de saisie",
        }}
      />
      <DiscardChangesModal
        open={isOpenModal}
        onClose={() => {
          setIsOpenModal(false);
          setCurrentPagePending();
          setSelectedDatePending();
        }}
        onValidate={() => {
          setSuccessMissions([]);
          setChangedMissionsIds([]);
          setErrorMissions([]);
          if (currentPagePending) {
            onValidateChangePage();
          } else if (selectedDatePending) {
            onValidateChangeDate();
          }
          setIsOpenModal(false);
        }}
      />
      <SpreadsheetFilters
        filters={filters}
        setFilters={setFilters}
        getNamesByFilters={getNamesByFilters}
        namesByFilters={namesByFilters}
        getClients={getClientsByName}
        getOptions={getPersonsByProfile}
        personsByProfileList={personsByProfileList}
        branchList={branchList}
        visibleColumns={visibleColumns.filter(
          (elm) => elm.value !== "mission_name"
        )}
        setVisibleColumns={setVisibleColumns}
        searchWithFilters={searchWithFilters}
        faesFilter={faesFilter}
        setFaesFilter={setFaesFilter}
        selectedDateObj={selectedDateObj}
        setCurrentPage={setCurrentPage}
      />
      {loadMissionStatus === QUERY_STATUS.LOADING ? (
        <Loader key={"loadMissions"} />
      ) : (
        <>
          <ScrollToTop />
          <SpreadsheetTable
            missionsList={missionBillsByParameters}
            visibleColumns={sortVisibleColumns(visibleColumns)}
            selectedDate={selectedDate_}
            setSelectedDate={(date) => {
              setSelectedDate_(date);
              setCurrentPage(1);
            }}
            saveSpreadsheetChanges={saveSpreadsheetChanges}
            getMission={getMission}
            clientsList={clientsByName}
            getClients={getClientsByName}
            branches={branchList}
            successMissions={successMissions}
            errorMissions={errorMissions}
            setSuccessMissions={setSuccessMissions}
            changedMissionsIds={changedMissionsIds}
            setChangedMissionsIds={setChangedMissionsIds}
            clonedMissionList={clonedMissionList}
            setClonedMissionList={setClonedMissionList}
            selectedDatePending={selectedDatePending}
            setSelectedDatePending={setSelectedDatePending}
            editMissionStatus={editMissionStatus}
            apiErrorResponse={apiErrorResponse}
            setErrorMissions={setErrorMissions}
            status={editMissionStatus}
            setStoredSpreadsheetInfos={setStoredSpreadsheetInfos}
            filters={filters}
            currentPage={currentPage}
            renderNoResultsMessage={renderNoResultsMessage}
            namesByFilters={namesByFilters}
            userScope={userScope}
          />
        </>
      )}
      {missionBillsByParameters.length > 0 &&
        loadMissionStatus !== QUERY_STATUS.LOADING && (
          <div className="justify-content-space-between">
            <Pagination
              total_pages={total}
              getCurrentPageContent={getCurrentPageContent}
              page={currentPage}
            />
            <Button
              color="btn-medium gray-700"
              id="button-search"
              onClick={() => {
                saveSpreadsheetChanges();
                window.scrollTo(0, 0);
              }}
            >
              <FormattedMessage
                id="save_all"
                defaultMessage="Sauvegarder les modifications"
              />
            </Button>
          </div>
        )}
    </div>
  );
};

export default Spreadsheet;
